import { Outlet } from 'react-router-dom';
import useCanonicalURL, { useRobotsReset, useFAQSchemaDedupe } from '@/hooks/useSEO';
import useStudyAbroadAutoMeta from '@/hooks/useStudyAbroadAutoMeta';
import StudyAbroadNavbar from './components/StudyAbroadNavbar';
import StudyAbroadFooter from './components/StudyAbroadFooter';
import StickyEligibilityCTA from './components/StickyEligibilityCTA';
import MobileProfileEvalPill from './components/MobileProfileEvalPill';
import WhatsAppButton from '@/components/common/WhatsAppButton';
import './study-abroad.css';

// Master shell for the /study-abroad/ sandbox.
// `zaf-edu-platform` is the CSS scope wrapper — ALL study-abroad styles live under this class
// and never bleed into the recruitment (ZAFCO HRM) pages.
//
// The floating WhatsApp button is mounted here AND in the recruitment App.js;
// because both reads from `useContactChannel()`, the SA mount will resolve to
// the Study Abroad number (9990533327) automatically based on the route.
const StudyAbroadLayout = () => {
  // SEO: route-aware self-referencing canonical + og:url for EVERY SA page.
  // This is the single defensive default — individual pages can still override
  // title / description via usePageMeta(...) but the canonical is guaranteed
  // self-referential regardless. Fixes the systemic Googlebot "URL Unknown to
  // Google" indexing failure documented in /app/audit/seo/SEO_INDEXING_AUDIT.md.
  useCanonicalURL();

  // SEO: path-aware Title + Meta-Description engine for the entire silo.
  // Mounted at layout level so ANY current OR future route under
  // /study-abroad/* gets a Study-Abroad-branded title automatically — even
  // orphan pages that forget to call usePageMeta(). Pages that DO call
  // usePageMeta() still win because their regular useEffect fires AFTER
  // this hook's useLayoutEffect (correct cascade: child > layout fallback).
  // Eliminates the recruitment-site title leak that was triggering GSC
  // duplicate-title penalties across the entire Study Abroad silo (Feb 2026).
  useStudyAbroadAutoMeta();

  // SEO safety net — strip any stray <meta name="robots"> left behind by
  // admin/404 pages so Study Abroad public URLs are never tagged noindex.
  useRobotsReset();

  // SEO safety net — on every route change, deduplicate FAQPage JSON-LD
  // scripts in the document head. Fixes "Duplicate field FAQPage" GSC Rich
  // Results errors caused by legacy pages that inline-inject FAQ schemas
  // with inconsistent markers (Feb 2026).
  useFAQSchemaDedupe();

  return (
    <div className="zaf-edu-platform min-h-screen bg-white" data-testid="study-abroad-layout">
      <StudyAbroadNavbar />
      <main>
        <Outlet />
      </main>
      <StudyAbroadFooter />
      <StickyEligibilityCTA />
      <MobileProfileEvalPill />
      <WhatsAppButton />
    </div>
  );
};

export default StudyAbroadLayout;
